import { getSupabaseClient } from './supabase';

/**
 * Mapeamento de fallback entre slugs amigáveis e URLs de salas Daily.
 * Usado apenas quando Supabase não está disponível.
 *
 * A URL da sala está armazenada URLEncoded para reproduzir o cenário atual.
 */
export const ROOM_URL_FALLBACK = {
  'aaa-aaa-aaa': 'https%3A%2F%2Fguarachat.daily.co%2F3xhYadzfQs2nVfVyKJLw',
};

/**
 * Busca informações completas da sala no Supabase pelo slug (com JOIN com usuários).
 * Retorna a URL da sala e dados dos participantes.
 *
 * @param {string} slug - Slug amigável da sala
 * @returns {Promise<object|null>} Objeto com daily_room_url e participantes, ou null se não encontrada
 */
export async function getRoomDataFromSlug(slug) {
  if (!slug) return null;

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return null;

    // Usando a view 'room_members_with_users' criada no Supabase
    const { data, error } = await supabase
      .from('room_members_with_users')
      .select('*')
      .eq('slug', slug);

    // Opção alternativa: JOIN direto (caso não queira usar a view)
    // Descomente e ajuste se necessário:
    /*
    const { data, error } = await supabase
      .from('room_members')
      .select(`
        *,
        profiles (
          id,
          full_name,
          email
        )
      `)
      .eq('slug', slug);
    */

    if (error) {
      // Se o JOIN falhar, tenta sem JOIN (fallback)
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('room_members')
        .select('*')
        .eq('slug', slug)
        .single();

      if (!fallbackError && fallbackData) {
        return {
          daily_room_url: fallbackData.daily_room_url,
          participants: [{
            user_id: fallbackData.user_id,
            session_id: fallbackData.session_id,
          }],
        };
      }
      throw error;
    }

    if (data && data.length > 0) {
      // Se retornou array, agrupa todos os participantes da mesma sala
      const allParticipants = Array.isArray(data) ? data : [data];
      const firstParticipant = allParticipants[0];

      return {
        daily_room_url: firstParticipant.daily_room_url,
        slug: firstParticipant.slug,
        session_id: firstParticipant.session_id,
        participants: allParticipants.map((p) => ({
          user_id: p.user_id,
          session_id: p.session_id,
          // A view já traz user_name e user_email diretamente
          user_name: p.user_name || null,
          user_email: p.user_email || null,
        })),
      };
    }

    return null;
  } catch (err) {
    return null;
  }
}

/**
 * Busca a URL da sala no Supabase pelo slug.
 * Se Supabase não estiver disponível, usa o fallback.
 *
 * @param {string} slug - Slug amigável da sala
 * @returns {Promise<string|null>} URL da sala ou null se não encontrada
 */
export async function getRoomUrlFromSlug(slug) {
  if (!slug) return null;

  // Tenta buscar dados completos primeiro
  const roomData = await getRoomDataFromSlug(slug);
  if (roomData && roomData.daily_room_url) {
    return roomData.daily_room_url;
  }

  // Fallback para busca simples (sem JOIN)
  try {
    const supabase = await getSupabaseClient();
    if (supabase) {
      const { data, error } = await supabase
        .from('room_members')
        .select('daily_room_url')
        .eq('slug', slug)
        .single();

      if (!error && data && data.daily_room_url) {
        return data.daily_room_url;
      }
    }
  } catch (err) {
    // Erro ao buscar sala no Supabase, usando fallback
  }

  // Fallback final
  const encoded = ROOM_URL_FALLBACK[slug];
  if (!encoded) return null;
  try {
    return decodeURIComponent(encoded);
  } catch (err) {
    return null;
  }
}

/**
 * Busca o slug no Supabase pela URL da sala.
 * Se Supabase não estiver disponível, usa o fallback.
 *
 * @param {string} roomUrl - URL da sala Daily
 * @returns {Promise<string|null>} Slug ou null se não encontrado
 */
export async function getSlugFromRoomUrl(roomUrl) {
  if (!roomUrl) return null;

  // Tenta buscar no Supabase primeiro
  try {
    const supabase = await getSupabaseClient();
    if (supabase) {
      const { data, error } = await supabase
        .from('room_members')
        .select('slug')
        .eq('daily_room_url', roomUrl)
        .single();

      if (!error && data && data.slug) {
        return data.slug;
      }
    }
  } catch (err) {
    // Erro ao buscar slug no Supabase, usando fallback
  }

  // Fallback
  const entries = Object.entries(ROOM_URL_FALLBACK);
  const found = entries.find((entry) => {
    const [, encoded] = entry;
    try {
      return decodeURIComponent(encoded) === roomUrl;
    } catch (err) {
      // Ignora erros de decodificação
      return false;
    }
  });
  return found ? found[0] : null;
}

